import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Paperclip, Camera, X, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg, todayIST } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Field, Combobox } from "@/components/common";

const LINE = { product_id: "", drum_no: "", quantity: "", rate: "", rack: "" };

export function AttachmentPicker({ files, setFiles, testid = "attach" }) {
  const [busy, setBusy] = useState(false);
  const up = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    const fd = new FormData();
    list.forEach((f) => fd.append("files", f));
    setBusy(true);
    try { const { data } = await api.post("/attachments", fd); setFiles([...files, ...data]); toast.success(`${data.length} file(s) uploaded`); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
    e.target.value = "";
  };
  const accept = ".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx";
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <label className="inline-flex items-center gap-2 h-10 px-3 rounded-md border cursor-pointer text-sm hover:bg-muted" data-testid={`${testid}-file-btn`}>
          <Paperclip className="h-4 w-4" />{busy ? "Uploading…" : "Attach files"}
          <input type="file" multiple accept={accept} hidden onChange={up} data-testid={`${testid}-file-input`} />
        </label>
        <label className="inline-flex items-center gap-2 h-10 px-3 rounded-md border cursor-pointer text-sm hover:bg-muted" data-testid={`${testid}-camera-btn`}>
          <Camera className="h-4 w-4" />Camera
          <input type="file" accept="image/*" capture="environment" hidden onChange={up} />
        </label>
      </div>
      <div className="flex flex-wrap gap-2">
        {files.map((f) => (
          <span key={f.id} className="inline-flex items-center gap-1 rounded border px-2 py-1 text-xs" data-testid={`${testid}-chip`}>
            {f.filename}<button type="button" onClick={() => setFiles(files.filter((x) => x.id !== f.id))}><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground">JPG, PNG, WEBP, PDF, DOC, DOCX · max 10 MB each · stored privately</p>
    </div>
  );
}

export default function InwardNew() {
  const { can, lookups, user } = useAuth();
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [h, setH] = useState({ date: todayIST(), supplier_id: "", invoice_no: "", po_no: "", vehicle_no: "", transporter: "", lr_no: "", received_by: user?.name || "", verified_by: "", warehouse: "", remarks: "" });
  const [lines, setLines] = useState([{ ...LINE }]);
  const [files, setFiles] = useState([]);
  const [postNow, setPostNow] = useState(can("inward:post"));
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(false);
  useEffect(() => {
    api.get("/products", { params: { status: "active" } }).then((r) => setProducts(r.data));
    api.get("/suppliers").then((r) => setSuppliers(r.data));
  }, []);
  useEffect(() => { if (lookups && !h.warehouse) setH((p) => ({ ...p, warehouse: lookups.warehouses[0] || "" })); }, [lookups]); // eslint-disable-line react-hooks/exhaustive-deps
  const pmap = Object.fromEntries(products.map((p) => [p.id, p]));
  const setL = (i, k, v) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const sh = (k) => (e) => setH({ ...h, [k]: e.target.value });

  const submit = async () => {
    setBusy(true);
    try {
      const body = { ...h, post_now: postNow, attachment_ids: files.map((f) => f.id),
        lines: lines.map((l) => ({ product_id: l.product_id, drum_no: l.drum_no, quantity: +l.quantity, rate: +(l.rate || 0), unit: pmap[l.product_id]?.uom || "", rack: l.rack })) };
      const { data } = await api.post("/inwards", body);
      toast.success(`${data.inward_no} ${data.status === "posted" ? "posted — stock updated" : "submitted for posting"}`);
      nav(`/inward/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const readBill = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setReading(true);
    try {
      const { data } = await api.post("/ai/read-invoice", fd);
      setH((p) => ({ ...p, supplier_id: data.supplier_id || p.supplier_id, invoice_no: data.invoice_no || p.invoice_no, date: data.date || p.date, vehicle_no: data.vehicle_no || p.vehicle_no }));
      if (data.lines.length) setLines(data.lines.map((l) => ({ ...LINE, product_id: l.product_id, drum_no: l.drum_no, quantity: l.quantity ? String(l.quantity) : "", rate: l.rate ? String(l.rate) : "", hint: l.text })));
      toast.success(`Read ${data.lines.length} line(s)${data.supplier_id ? "" : `. Supplier "${data.supplier_name}" not in list — select it`}. Please check every line.`);
    } catch (err) { toast.error(errMsg(err)); }
    setReading(false);
  };

  const prodOpts = products.map((p) => ({ value: p.id, label: `${p.brand} · ${p.cable_size}${p.cores ? ` × ${p.cores}C` : ""} · ${p.sku}` }));
  return (
    <div className="space-y-6 pb-24">
      <PageHeader title="New Stock Inward" subtitle="One entry can receive multiple products and drums against one supplier invoice.">
        <label className="inline-flex items-center gap-2 h-10 px-4 rounded-md border cursor-pointer text-sm font-medium hover:bg-muted transition-colors" data-testid="inward-ai-read-btn">
          <Sparkles className="h-4 w-4 text-red-600" />{reading ? "Reading bill…" : "Read bill photo with AI"}
          <input type="file" accept="image/jpeg,image/png,image/webp" capture="environment" hidden disabled={reading} onChange={readBill} data-testid="inward-ai-file-input" />
        </label>
      </PageHeader>
      <section className="rounded-lg border bg-card p-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Field label="Date" required><Input type="date" value={h.date} max={todayIST()} onChange={sh("date")} data-testid="inward-date-input" /></Field>
        <Field label="Supplier" required className="col-span-2 lg:col-span-1"><Combobox options={suppliers.map((s) => ({ value: s.id, label: s.name }))} value={h.supplier_id} onChange={(v) => setH({ ...h, supplier_id: v })} testid="inward-supplier-select" placeholder={suppliers.length ? "Select supplier" : "Add suppliers first"} /></Field>
        <Field label="Supplier Invoice No" required><Input value={h.invoice_no} onChange={sh("invoice_no")} data-testid="inward-invoice-input" /></Field>
        <Field label="Purchase Order No"><Input value={h.po_no} onChange={sh("po_no")} data-testid="inward-po-input" /></Field>
        <Field label="Vehicle Number"><Input value={h.vehicle_no} onChange={(e) => setH({ ...h, vehicle_no: e.target.value.toUpperCase() })} placeholder="KA01AB1234" data-testid="inward-vehicle-input" /></Field>
        <Field label="Transporter"><Input value={h.transporter} onChange={sh("transporter")} data-testid="inward-transporter-input" /></Field>
        <Field label="LR Number"><Input value={h.lr_no} onChange={sh("lr_no")} data-testid="inward-lr-input" /></Field>
        <Field label="Warehouse"><Combobox options={(lookups?.warehouses || []).map((w) => ({ value: w, label: w }))} value={h.warehouse} onChange={(v) => setH({ ...h, warehouse: v })} testid="inward-warehouse-select" /></Field>
        <Field label="Received By"><Input value={h.received_by} onChange={sh("received_by")} data-testid="inward-received-by-input" /></Field>
        <Field label="Verified By"><Input value={h.verified_by} onChange={sh("verified_by")} data-testid="inward-verified-by-input" /></Field>
        <Field label="Remarks" className="col-span-2"><Textarea rows={1} value={h.remarks} onChange={sh("remarks")} data-testid="inward-remarks-input" /></Field>
      </section>

      <section className="space-y-2">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Products / drums received</div>
        {lines.map((l, i) => {
          const p = pmap[l.product_id];
          return (
            <div key={i} className="rounded-lg border bg-card p-3 grid grid-cols-2 md:grid-cols-12 gap-2 items-end" data-testid={`inward-line-${i}`}>
              <Field label={`Line ${i + 1} · Brand / Size / Type`} className="col-span-2 md:col-span-4"><Combobox options={prodOpts} value={l.product_id} onChange={(v) => setL(i, "product_id", v)} testid={`inward-line-${i}-product`} placeholder="Search product" /></Field>
              <Field label="Drum Number" className="md:col-span-2"><Input value={l.drum_no} onChange={(e) => setL(i, "drum_no", e.target.value.toUpperCase())} placeholder={p?.uom === "m" ? "Required" : "Optional"} data-testid={`inward-line-${i}-drum`} /></Field>
              <Field label={`Qty (${p?.uom || "unit"})`} className="md:col-span-2"><Input type="number" inputMode="decimal" step="0.001" min="0" value={l.quantity} onChange={(e) => setL(i, "quantity", e.target.value)} data-testid={`inward-line-${i}-qty`} /></Field>
              {can("cost:view") && <Field label="Rate ₹/unit" className="md:col-span-2"><Input type="number" step="0.01" min="0" value={l.rate} onChange={(e) => setL(i, "rate", e.target.value)} data-testid={`inward-line-${i}-rate`} /></Field>}
              <Field label="Rack" className="md:col-span-1"><Input value={l.rack} onChange={(e) => setL(i, "rack", e.target.value)} data-testid={`inward-line-${i}-rack`} /></Field>
              <Button variant="ghost" size="icon" disabled={lines.length === 1} onClick={() => setLines(lines.filter((_, j) => j !== i))} data-testid={`inward-line-${i}-remove`}><Trash2 className="h-4 w-4" /></Button>
              {l.hint && <div className={`col-span-2 md:col-span-12 text-[11px] ${l.product_id ? "text-muted-foreground" : "text-red-600"}`}>Bill says: {l.hint}{l.product_id ? "" : " — choose the product"}</div>}
            </div>
          );
        })}
        <Button variant="outline" onClick={() => setLines([...lines, { ...LINE }])} data-testid="inward-add-line-btn"><Plus className="h-4 w-4 mr-1" />Add drum / line</Button>
      </section>

      <section className="rounded-lg border bg-card p-4 space-y-2">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">Bill / invoice attachment</div>
        <AttachmentPicker files={files} setFiles={setFiles} testid="inward-attach" />
      </section>

      <div className="fixed bottom-16 lg:bottom-0 left-0 right-0 lg:left-64 z-30 border-t bg-background/95 backdrop-blur px-4 sm:px-8 py-3 flex flex-wrap items-center gap-4 justify-end">
        {can("inward:post") && <label className="flex items-center gap-2 text-sm"><Checkbox checked={postNow} onCheckedChange={(v) => setPostNow(!!v)} data-testid="inward-post-now-checkbox" />Post to stock immediately</label>}
        <Button variant="outline" onClick={() => nav("/inward")} data-testid="inward-cancel-btn">Cancel</Button>
        <Button className="bg-red-600 hover:bg-red-700 text-white min-w-40" disabled={busy} onClick={submit} data-testid="inward-submit-btn">{busy ? "Saving…" : postNow ? "Save & post" : "Submit for posting"}</Button>
      </div>
    </div>
  );
}
